import React, { useEffect, useMemo, useState } from 'react'
import { Stack } from '@mantine/core'
import { useSelector } from 'react-redux'
import ExpandableMantineTable from './trade-enty-table'
import SectionTitle from 'app/views/components/Headers&Text/SectionTitle'
import PlusButtonWithPopover from './plus-button-modal'
import { filterByTradeDate } from 'app/utils/util-functions'
import { SUB_TRADE_TERMINATED_STATUS } from './utils/master-trade-status'
import { getBusinessDate } from 'app/state/ducks/business-date/selectors'
import { getCalendars } from 'app/state/ducks/holiday-calendars/selectors'
import { HolidayCalendarParams } from 'app/models/holiday-calendar-params'
import { loadTradeFiltersFromStorage } from 'app/utils/grid-sorting-storage'
import { Hold, HoldSystem } from 'app/models/hold-params'
import { getUnreadCounts } from 'app/state/ducks/trade-notes/selectors'
import { getTradeList, getTradeListLoader } from 'app/state/ducks/trade-list/selectors'
import { TradeListItem, TradeListSubTrade } from 'app/models/trade-list-params'
import { buildTradeListRows, matchesSettlementDate, TradeListRow } from './utils/trade-list-rows'

export interface FilterValues {
  tradeStatus?: string[];
  tradeDate?: string | Date[] | null[];
  settlementDate?: string | Date[] | null[];
  tradeAction?: string[];
  deal?: string[];
  /**
   * Matches an entity on either SIDE of the trade — buyer or seller of the master
   * trade or its sub-trades. Supersedes myEntity and counterparty; the agent is not
   * a side, so the Agent filter stays independent.
   */
  entityAnySide?: string[];
  myEntity?: string[];
  counterparty?: string[];
  agent?: string[];
  paymentMethod?: string[];
  aaStatus?: string[];
  unappliedTransactions?: string;
  /** 'Locked' | 'Unlocked' — an active LendOS-placed hold on the trade. */
  lockStatus?: string;
  /** 'On Hold' | 'Not On Hold' — an active Signing-Portal-placed (Agent) hold. */
  holdStatus?: string;
  allocated?: string;
  /** 'Actioned' | 'Unactioned' — actioned state of a trade's Activity Stream notes. */
  activityStream?: string;
  /** 'Stale' | 'Not Stale' — staleness of the trade's sent Open delivery packet. */
  staleDocuments?: string;
  documentsNotRefreshed?: string;

}

// Minimal shape the Entity (Any Side) match needs — anything carrying a buyer
// and a seller, so master trades and sub-trades both satisfy it.
type TradeSides = {
  buyerId?: { id?: string }
  sellerId?: { id?: string }
}

/**
 * True when any of `entityIds` sits on either SIDE of the trade — buyer or seller
 * of the master trade, or of any of its sub-trades.
 *
 * Covers exactly the ground the myEntity and counterparty filters cover between
 * them, and nothing more: the deal's AGENT is not a side of the trade and is
 * deliberately excluded, so the Agent filter stays independent and can be combined
 * with this one.
 *
 * Unlike myEntity / counterparty this ignores `trade.myEntity` entirely — the point
 * of "any side" is that the user need not know which side they were on. Exported
 * for tests.
 */
export function matchesEntityAnySide(
  entityIds: ReadonlyArray<string>,
  trade: TradeSides,
  linkedSubTrades: ReadonlyArray<TradeSides>,
): boolean {
  return entityIds.some(id =>
    trade.buyerId?.id === id ||
    trade.sellerId?.id === id ||
    linkedSubTrades.some(st => st.buyerId?.id === id || st.sellerId?.id === id)
  );
}

/**
 * Activity Stream actioned-state membership (LOS-6987; the options were Read/Unread).
 *
 * 'Unactioned' = at least one unactioned note. 'Actioned' = nothing unactioned, which
 * INCLUDES a trade with no notes at all (product decision — a trade with nothing
 * outstanding belongs with the ones you have dealt with).
 *
 * The two options are therefore complements and partition the whole list, so note
 * *presence* no longer matters and only the unread count is needed.
 *
 * Exported for tests.
 */
export function matchesActivityStream(
  selection: string,
  unreadNotes: number,
): boolean {
  // An unrecognised value (e.g. stale localStorage) filters nothing, matching the
  // fall-through of every other branch in the filter loop. That includes a 'Read' or
  // 'Unread' saved by a pre-rename build.
  if (selection !== 'Actioned' && selection !== 'Unactioned') return true;
  return selection === 'Unactioned' ? unreadNotes > 0 : unreadNotes === 0;
}

/**
 * Stale Documents membership.
 *
 * 'Stale' = at least one sent Open packet is stale. 'Not Stale' = everything else,
 * which INCLUDES a trade whose delivery document was never created (product
 * decision — nothing sent cannot have gone stale).
 *
 * The two options are therefore complements and partition the whole list.
 *
 * Exported for tests.
 */
export function matchesStaleDocuments(
  selection: string,
  isStale: boolean,
): boolean {
  // Unrecognised value (e.g. stale localStorage) filters nothing.
  if (selection !== 'Stale' && selection !== 'Not Stale') return true;
  return selection === 'Stale' ? isStale : !isStale;
}

/**
 * True when the trade carries an active hold placed by `system`.
 *
 * Deliberately ignores `origin.level`: the filters ask "is this trade held at all",
 * so a facility- or deal-level hold cascaded down to the trade's signable documents
 * counts. That is the difference from the per-level gating helpers (e.g.
 * tradeAlreadyLocked in trade-detail-actions), which do pin the level because they
 * gate one level's action.
 *
 * `trade.holds` is derived server-side from EVERY signable document on the
 * trade's sub-trades (trade-list.facts.ts), so both filters see the same full
 * set. The liftedAt re-check is belt-and-braces — the server already drops
 * lifted holds, but this is exported and may be handed a raw list.
 */
const hasActiveHoldFrom = (
  trade: { holds?: Hold[] | null },
  system: HoldSystem,
): boolean => !!trade.holds?.some(h => !h.liftedAt && h.system === system);

/**
 * lockStatus filter predicate: a "Lock" is a LendOS-placed hold.
 *
 * Previously any active hold counted, which conflated the two systems — an Agent
 * Hold placed in the Signing Portal made a trade read as "Locked" in LendOS. The UI
 * copy has always kept them apart ("Trade Lock" vs "Agent Hold", see hold-title.ts);
 * the filter now does too.
 *
 * Exported for tests.
 */
export function matchesLockStatus(
  trade: { holds?: Hold[] | null },
  value: string,
): boolean {
  // Unrecognised value filters nothing — see matchesHoldStatus.
  if (value !== 'Locked' && value !== 'Unlocked') return true;
  const isLocked = hasActiveHoldFrom(trade, 'LendOS');
  return value === 'Locked' ? isLocked : !isLocked;
}

/**
 * holdStatus filter predicate: a "Hold" is a Signing-Portal-placed hold (an Agent
 * Hold). The mirror of matchesLockStatus — same data, opposite system.
 *
 * Exported for tests.
 */
export function matchesHoldStatus(
  trade: { holds?: Hold[] | null },
  value: string,
): boolean {
  // Unrecognised value filters nothing. Without this an old 'Held' left in
  // localStorage by a pre-rename build would fall into the negative branch and
  // silently hide every held trade.
  if (value !== 'On Hold' && value !== 'Not On Hold') return true;
  const isHeld = hasActiveHoldFrom(trade, 'SigningPortal');
  return value === 'On Hold' ? isHeld : !isHeld;
}

/**
 * Trade Status filter predicate (LOS-7001). The options are MASTER trade statuses — Submitted,
 * Open, Closed (ledger `Settled`), Canceled — matched against the trade's own status, plus
 * **Terminated**, which exists only at SUB-trade level: it matches a trade with at least one
 * Terminated sub-trade. Several selections are an OR, so a trade matching any of them is kept
 * (once — this is a per-trade predicate).
 *
 * A sub-trade's own status is its lockedStatus (Settled | Canceled | Terminated) once locked;
 * an open one carries none.
 *
 * Exported for tests.
 */
export function matchesTradeStatus(
  selection: string[],
  trade: {
    status?: string
    subTrades: ReadonlyArray<Pick<TradeListSubTrade, 'status' | 'lockedStatus'>>
  },
): boolean {
  if (trade.status !== undefined && selection.includes(trade.status)) return true;
  return (
    selection.includes(SUB_TRADE_TERMINATED_STATUS) &&
    trade.subTrades.some(st => st.lockedStatus === SUB_TRADE_TERMINATED_STATUS)
  );
}

/**
 * The Trade List page.
 *
 * Rows come from ONE request, GET /api/trade-list, already joined, folded and
 * — for every filter — derived server-side: holds, AA statuses, allocation,
 * unapplied transactions, not-refreshed and stale documents, and each sub
 * trade's settlement dates. The page adds what the server cannot: the
 * displayed settlement date and T+ (business date, calendars) and the
 * Activity Stream read state (unread counts, the portal's own database).
 *
 * Every filter runs on the row. No filter loads a store.
 */
// The row carries each sub trade's two sides as two id arrays. matchesEntityAnySide
// takes a list of {buyerId, sellerId}; which side is which does not matter to it,
// so each id becomes a one-sided entry.
const subTradeSides = (row: TradeListRow) => [
  ...row.subTradeMyEntityIds.map(id => ({ buyerId: { id } })),
  ...row.subTradeCounterpartyIds.map(id => ({ sellerId: { id } })),
]

export function TradeEntry() {
  const tradeListItems: TradeListItem[] = useSelector(getTradeList)
  const tradeListLoader = useSelector(getTradeListLoader)
  // SD / T+ and Stream: the two things the row cannot carry.
  const businessDate = useSelector(getBusinessDate)
  const holidayCalendars: HolidayCalendarParams[] = useSelector(getCalendars)
  const unreadCounts = useSelector(getUnreadCounts)

  const [globalLoading, setGlobalLoading] = useState(false);
  const [filterValues, setFilterValues] = useState<FilterValues>(() => loadTradeFiltersFromStorage());
  const [filteredRows, setFilteredRows] = useState<TradeListRow[]>([]);
  const rows = useMemo<TradeListRow[]>(
    () => buildTradeListRows(tradeListItems, { businessDate, holidayCalendars }),
    [tradeListItems, businessDate, holidayCalendars],
  );

  useEffect(() => {
    setFilteredRows(
      rows.filter((trade) => {
        return Object.entries(filterValues || {}).every(([key, value]) => {
          if (!value) return true;
          if (Array.isArray(value) && value.length === 0) return true;
          if (key === 'tradeStatus') return matchesTradeStatus(value as string[], trade);
          if (key === 'tradeDate') return filterByTradeDate(value as [Date | null, Date | null] | [], trade.tradeDate ?? '');
          if (key === 'settlementDate') {
            return matchesSettlementDate(value as [Date | null, Date | null] | [], trade, { businessDate, holidayCalendars });
          }
          if (key === 'tradeAction') {
            const side = trade.myEntity.toLowerCase();
            return (value as string[]).some(v => v.toLowerCase() === side);
          }
          if (key === 'deal') return (value as string[]).includes(trade.dealId?.id ?? '');
          // Entity filters run on the row: the projection carries the master's
          // two sides and, per sub trade, the entity on its own side and on the
          // other side (distinct per master).
          if (key === 'entityAnySide') {
            return matchesEntityAnySide(value as string[], trade, subTradeSides(trade));
          }
          if (key === 'myEntity') {
            const mySide = trade.myEntity === 'Buy' ? trade.buyerId?.id : trade.sellerId?.id;
            return (value as string[]).some(v => mySide === v || trade.subTradeMyEntityIds.includes(v));
          }
          if (key === 'counterparty') {
            const otherSide = trade.myEntity === 'Buy' ? trade.sellerId?.id : trade.buyerId?.id;
            return (value as string[]).some(v => otherSide === v || trade.subTradeCounterpartyIds.includes(v));
          }
          if (key === 'agent') {
            return (value as string[]).some(v => trade.dealAgentIds.includes(v));
          }
          if (key === 'paymentMethod') {
            return trade.paymentType != null && (value as string[]).includes(trade.paymentType);
          }
          if (key === 'aaStatus') {
            // Any sub trade of this master trade with a matching AA status — the
            // row carries the distinct statuses across its Assignment Agreements.
            return trade.aaStatuses.some(status => (value as string[]).includes(status));
          }
          if (key === 'unappliedTransactions') {
            return value === 'Yes' ? trade.hasUnappliedTransactions : !trade.hasUnappliedTransactions;
          }
          if (key === 'documentsNotRefreshed') {
            return value === 'Yes' ? trade.hasNotRefreshedDocuments : !trade.hasNotRefreshedDocuments;
          }
          if (key === 'lockStatus') {
            return matchesLockStatus(trade, value as string);
          }
          if (key === 'holdStatus') {
            return matchesHoldStatus(trade, value as string);
          }
          if (key === 'staleDocuments') {
            return matchesStaleDocuments(value as string, trade.hasStaleDocuments);
          }
          if (key === 'activityStream') {
            return matchesActivityStream(
              value as string,
              unreadCounts.byMasterTrade?.[trade.id] ?? 0,
            );
          }
          if (key === 'allocated') {
            return value === 'Yes' ? trade.isFullyAllocated : !trade.isFullyAllocated;
          }
          return true;
        });
      })
    );
  }, [rows, filterValues, businessDate, holidayCalendars, unreadCounts]);

  useEffect(() => {
    setGlobalLoading(tradeListLoader);
  }, [tradeListLoader]);

  return (
    <Stack>
      <SectionTitle>Filter</SectionTitle>
      <Stack w='100%' className='sectionRemoveGap' style={{ backgroundColor: '#f7f7f7', borderBottom: '1px solid #B4B3B3', borderLeft: '1px solid #B4B3B3', borderRight: '1px solid #B4B3B3' }}>
        <PlusButtonWithPopover filterValues={filterValues} setFilterValues={setFilterValues} ></PlusButtonWithPopover>
      </Stack>
      <div style={{ marginBottom: '20px', marginTop: '10px' }}>
        <ExpandableMantineTable title={'Trade List'} rows={filteredRows} globalLoading={globalLoading} filterValues={filterValues} />
      </div>
    </Stack >
  )
}
